import React, { Fragment, useState, useEffect } from 'react'
import { deburr, debounce } from 'lodash'

import DropDownButton from '@components/DropDownButton'

import { showToast } from '@services/toast'

import { formatToLocale } from '@utils/numberFormatter'

import {
  Container,
  Table,
  Row,
  ContentColumn,
  Column,
  Scroll,
  IconWrapper,
  GadgetsWrapper,
  Input,
  TableWrapper,
  Text,
  Option,
  AdUrl,
} from './styles'

import { Arrow, Magnifier, List, Radio, Checkbox } from '@icons'

const FIELDS_LABELS = {
  real_state_agency: 'Imobiliária',
  building: 'Edifício',
  address: 'Endereço',
  district: 'Bairro',
  price: 'Valor (R$)',
  sqr_mtr_price: 'Valor m² (R$)',
  area: 'Área',
  similarity: 'Índice de Similaridade',
}

const NUMERIC_FIELDS = [
  'price',
  'sqr_mtr_price',
  'area',
  'ad_age',
  'garages',
  'bedrooms',
  'similarity',

  'main_area',
  'main_bedrooms',
  'main_garages',
  'main_suites',
]

const ViewRealty = ({
  filters,
  allowUpdate,
  loading,
  selectedRealties = [],
  setSelectedRealties = () => null,
  ...props
}) => {
  const hasSimilarity = props.realties.some(x => x.similarity)
  const [firstColumn, setFirstColumn] = useState('real_state_agency')
  const [secondColumn, setSecondColumn] = useState('address')
  const [thirdColumn, setThirdColumn] = useState('sqr_mtr_price')
  const [sortBy, setSortBy] = useState(hasSimilarity ? 'similarity' : '')
  const [sortOrder, setSortOrder] = useState(hasSimilarity ? 'asc' : '')
  const [realties, setRealties] = useState(props.realties)
  const isAllRealtiesSelected =
    selectedRealties.length > 0 && selectedRealties.length === realties.length
  const isRealtySelected = id => selectedRealties.includes(id)

  const selectRealty = (id, all) => {
    let tempSelectedRealty = [...selectedRealties]

    if (all) {
      tempSelectedRealty = isAllRealtiesSelected
        ? []
        : realties.map(item => item.id)
    } else {
      if (isRealtySelected(id)) {
        tempSelectedRealty = tempSelectedRealty.filter(item => item !== id)
      } else {
        tempSelectedRealty.push(id)
      }
    }
    setSelectedRealties(tempSelectedRealty)
  }

  const renderActions = (on, text) => (
    <Fragment>
      <Radio selected={on} />
      <Text>{text}</Text>
    </Fragment>
  )

  const HEADER_ACTIONS = [
    [
      {
        label: renderActions(
          firstColumn === 'real_state_agency',
          FIELDS_LABELS.real_state_agency,
        ),
        action: () => setFirstColumn('real_state_agency'),
      },
      {
        label: renderActions(
          firstColumn === 'building',
          FIELDS_LABELS.building,
        ),
        action: () => setFirstColumn('building'),
      },
    ],
    [
      {
        label: renderActions(
          secondColumn === 'district',
          FIELDS_LABELS.district,
        ),
        action: () => setSecondColumn('district'),
      },
      {
        label: renderActions(secondColumn === 'address', FIELDS_LABELS.address),
        action: () => setSecondColumn('address'),
      },
    ],
    [
      {
        label: renderActions(
          thirdColumn === 'sqr_mtr_price',
          FIELDS_LABELS.price,
        ),
        action: () => setThirdColumn('sqr_mtr_price'),
      },
      {
        label: renderActions(
          thirdColumn === 'similarity',
          FIELDS_LABELS.similarity,
        ),
        action: () => setThirdColumn('similarity'),
      },
    ],
  ]

  const getAddress = (type, realty) => {
    const { city, district, number, street } = realty.address
    if (type === 'district') return district
    else return `${street}, ${number}, ${city}`
  }

  const numericSort = (a, b, sort, order) => {
    if (sort === 'ad_age') {
      const A = parseInt(a.ad_age, 0)
      const B = parseInt(b.ad_age, 0)

      if (order === 'asc') return A - B

      return B - A
    } else {
      if (order === 'asc') return a[sort] - b[sort]

      return b[sort] - a[sort]
    }
  }

  const sort = (sort, order = '') => {
    if (sort === sortBy) {
      order = sortOrder === 'asc' ? 'desc' : 'asc'
      setSortOrder(order)
    } else {
      order = 'asc'
      setSortOrder(order)
    }

    let newRealties = []
    if (NUMERIC_FIELDS.includes(sort)) {
      newRealties = realties.sort((a, b) => numericSort(a, b, sort, order))
    } else {
      if (sort === 'district') {
        newRealties = realties.sort((a, b) => {
          if (
            a.address.district?.toLowerCase() <
            b.address.district?.toLowerCase()
          )
            return -1

          return 1
        })
      } else if (sort === 'address') {
        newRealties = realties.sort((a, b) => {
          if (a.address.street?.toLowerCase() < b.address.street?.toLowerCase())
            return -1

          return 1
        })
      } else {
        newRealties = realties.sort((a, b) => {
          if (a[sort]?.toLowerCase() < b[sort]?.toLowerCase()) return -1

          return 1
        })
      }

      if (order === 'desc') newRealties.reverse()
    }

    setRealties(newRealties)
    setSortBy(sort)
  }

  const search = value => {
    if (value) {
      const parsedValue = deburr(value.toLowerCase())

      const searchedValuations = props.realties.filter(realty => {
        const realStateAgency = deburr(realty.real_state_agency).toLowerCase()
        const neighborhood = deburr(realty.address.district).toLowerCase()
        const address = deburr(
          `${realty.address.street.toLowerCase()}, ${realty.address.number}`,
        )
        const building = deburr(realty.building).toLowerCase()
        const adAge = String(realty.ad_age)
        const sqrMtrPrice = String(realty.sqr_mtr_price)
        const price = String(realty.price)
        const garages = String(realty.garages)
        const bedrooms = String(realty.bedrooms)
        const area = String(realty.area)

        return (
          adAge.includes(parsedValue) ||
          realStateAgency.includes(parsedValue) ||
          neighborhood.includes(parsedValue) ||
          address.includes(parsedValue) ||
          building.includes(parsedValue) ||
          sqrMtrPrice.includes(parsedValue) ||
          price.includes(parsedValue) ||
          garages.includes(parsedValue) ||
          bedrooms.includes(parsedValue) ||
          area.includes(parsedValue)
        )
      })

      setRealties(searchedValuations)
    } else {
      setRealties(props.realties)
    }
  }

  const debouncedSearch = debounce(search, 500)

  const handleRecentOnlyClick = async () => {
    if (loading) return
    await props.handleRecentOnlyClick()
  }

  useEffect(() => {
    if (props.realties) {
      setRealties(props.realties)
    }
  }, [props.realties])

  useEffect(() => {
    if (realties && hasSimilarity && sortOrder === 'asc') sort('similarity')
  }, [realties])

  return (
    <Container isLoading={loading}>
      <Row header alignEnd>
        {allowUpdate && (
          <Option
            isLoading={loading}
            title="Ao selecionar esta opção, a busca por amostra trará apenas anúncios que tiveram atualizações nos últimos 7 dias."
            onClick={handleRecentOnlyClick}
          >
            <Checkbox checked={filters.recentOnly} />
            <Text noMargin>Apenas anúncios recentes</Text>
          </Option>
        )}
        <GadgetsWrapper>
          <IconWrapper search>
            <Magnifier />
          </IconWrapper>
          <Input
            placeholder="Buscar nesta listagem"
            onChange={({ target: { value } }) => debouncedSearch(value)}
          />
        </GadgetsWrapper>
      </Row>

      <TableWrapper>
        {loading ? (
          'Carregando'
        ) : (
          <Table>
            <Row header>
              <ContentColumn>
                {props.selectable && (
                  <Column
                    columnWidth={6.5}
                    header
                    onClick={() => selectRealty('id', 'all')}
                  >
                    <Checkbox checked={isAllRealtiesSelected} />
                  </Column>
                )}
                <Column header>
                  <Text dropdown onClick={() => sort(firstColumn)}>
                    <Arrow
                      up={sortBy === firstColumn && sortOrder === 'asc'}
                      gray
                    />
                    {FIELDS_LABELS[firstColumn]}
                  </Text>
                  <DropDownButton
                    right
                    content={<List />}
                    actions={HEADER_ACTIONS[0]}
                  />
                </Column>
                <Column columnWidth={hasSimilarity ? 20.28 : 27.28} header>
                  <Text dropdown onClick={() => sort(secondColumn)}>
                    <Arrow
                      up={sortBy === secondColumn && sortOrder === 'asc'}
                      gray
                    />
                    {FIELDS_LABELS[secondColumn]}
                  </Text>
                  <DropDownButton
                    right
                    content={<List />}
                    actions={HEADER_ACTIONS[1]}
                  />
                </Column>
                <Column header>
                  <span onClick={() => sort('price')}>
                    <Arrow
                      up={sortBy === 'price' && sortOrder === 'asc'}
                      gray
                    />
                    Valor (R$)
                  </span>
                </Column>
                <Column header>
                  <span onClick={() => sort('sqr_mtr_price')}>
                    <Arrow
                      up={sortBy === 'sqr_mtr_price' && sortOrder === 'asc'}
                      gray
                    />
                    Valor m² (R$)
                  </span>
                </Column>
                {hasSimilarity && (
                  <Column header>
                    <span onClick={() => sort('similarity')}>
                      <Arrow
                        up={sortBy === 'similarity' && sortOrder === 'asc'}
                        gray
                      />
                      Índice de Similaridade (%)
                    </span>
                  </Column>
                )}
                <Column header>
                  <span onClick={() => sort('area')}>
                    <Arrow up={sortBy === 'area' && sortOrder === 'asc'} gray />
                    Área (m²)
                  </span>
                </Column>
                <Column header>
                  <span onClick={() => sort('bedrooms')}>
                    <Arrow
                      up={sortBy === 'bedrooms' && sortOrder === 'asc'}
                      gray
                    />
                    Quartos
                  </span>
                </Column>
                <Column header>
                  <span onClick={() => sort('garages')}>
                    <Arrow
                      up={sortBy === 'garages' && sortOrder === 'asc'}
                      gray
                    />
                    Garagem
                  </span>
                </Column>
                <Column columnWidth={props.selectable ? 9.28 : 11} header>
                  <span onClick={() => sort('ad_age')}>
                    <Arrow
                      up={sortBy === 'ad_age' && sortOrder === 'asc'}
                      gray
                    />
                    Idade do Anúncio
                  </span>
                </Column>
              </ContentColumn>
            </Row>

            <Scroll>
              {realties.map((realty, index) => {
                const firstColumnData = realty[firstColumn] || '-'
                const sqrMtrPrice = formatToLocale(realty.sqr_mtr_price)
                const similarity =
                  hasSimilarity && `${formatToLocale(realty.similarity)}%`
                const price = formatToLocale(realty.price) || '-'
                const address = getAddress(secondColumn, realty) || '-'
                const area = realty.area >= 0 ? realty.area : '-'
                const bedrooms = realty.bedrooms >= 0 ? realty.bedrooms : '-'
                const garages = realty.garages >= 0 ? realty.garages : '-'
                const adAge = `${
                  realty.ad_age >= 0 ? realty.ad_age : '-'
                } dia(s) `
                const url = realty.ad_url
                const selected = isRealtySelected(realty.id)
                const priceColor = realty.similarity_color

                return (
                  <Row key={index}>
                    <ContentColumn>
                      {props.selectable && (
                        <Column
                          columnWidth={6}
                          style={{ cursor: 'pointer' }}
                          title="Selecionar"
                          onClick={() => selectRealty(realty.id)}
                        >
                          <Checkbox checked={selected} />
                        </Column>
                      )}
                      <Column title={firstColumnData}>{firstColumnData}</Column>
                      <Column
                        columnWidth={hasSimilarity ? 20.28 : 27.28}
                        title={address}
                      >
                        {address}
                      </Column>
                      <Column title={price}>{price}</Column>
                      <Column
                        color={hasSimilarity ? priceColor : undefined}
                        title={sqrMtrPrice}
                      >
                        {sqrMtrPrice}
                      </Column>
                      {hasSimilarity && (
                        <Column
                          color={hasSimilarity ? priceColor : undefined}
                          title={similarity}
                        >
                          {similarity}
                        </Column>
                      )}
                      <Column title={area}>{area}</Column>
                      <Column title={bedrooms}>{bedrooms}</Column>
                      <Column title={garages}>{garages}</Column>
                      <Column title={adAge}>{adAge}</Column>
                    </ContentColumn>
                    <AdUrl
                      onClick={() =>
                        !url &&
                        showToast(
                          'A página deste anúncio está indisponível',
                          'warning',
                        )
                      }
                      href={url || '#'}
                      disabled={!url}
                      target="_blank"
                    >
                      Link
                    </AdUrl>
                  </Row>
                )
              })}
            </Scroll>
          </Table>
        )}
      </TableWrapper>
    </Container>
  )
}

export default ViewRealty
